import { useEffect, useState, Dispatch, SetStateAction } from 'react'
import styled, { css } from 'styled-components'
import { fadeIn, fadeOut, slideDown, slideUp } from '../../js/animation'
import { plusComma } from '../../js/script'
import { imgURL } from '../Footer'
import { ProductCoupon } from './OrderProductSection'
import { useOrderContext } from '../provider/OrderProvider'
import { OrderSheetCoupon } from '@shopby/shop-sdk'

type PropsType = {
  setIsModal: Dispatch<SetStateAction<boolean>>
  couponList: OrderSheetCoupon[]
  selectedProductNo: number | null
  selectedOptionNo: number | null
  onApplyCoupon?: (result: any) => void
}

const OrderCouponModal = ({
  setIsModal,
  couponList,
  selectedProductNo,
  selectedOptionNo,
  onApplyCoupon,
}: PropsType) => {
  const [closing, setClosing] = useState<boolean>(false)
  const [nowSelectedCoupon, setNowSelectedCoupon] = useState<ProductCoupon | null>(null)

  const { productCoupon } = useOrderContext()

  const closeModal = () => {
    setClosing(true)
    setTimeout(() => {
      setIsModal(false)
      setClosing(false)
    }, 280)
  }

  const handleApplyCoupon = () => {
    if (onApplyCoupon) onApplyCoupon(nowSelectedCoupon)
    closeModal()
  }

  useEffect(() => {
    if (!selectedProductNo || !selectedOptionNo) return
    const key = `${selectedProductNo}_${selectedOptionNo}`
    if (productCoupon[key]) setNowSelectedCoupon(productCoupon[key])
  }, [selectedOptionNo])

  return (
    <ModalWrap>
      <ModalBox closing={closing}>
        <ModalHeader>
          <HeaderTitle>
            사용 가능 쿠폰
            <HeaderCount>{couponList?.length}</HeaderCount>
          </HeaderTitle>
          <CloseButton className='btn_close' onClick={closeModal}>
            <img src={`${imgURL}/icon_close.png`} alt='닫기' />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <CouponBox>
            {couponList?.map((coupon: any, index: number) => {
              const isSelected = nowSelectedCoupon?.couponIssueNo === coupon?.couponIssueNo
              return (
                <div key={index}>
                  <CouponRadio type='radio' id={`coupon${index}`} name='coupon_radio' checked={isSelected} readOnly />
                  <CouponLabel
                    htmlFor={`coupon${index}`}
                    $selected={isSelected}
                    onClick={(e) => {
                      e.preventDefault()
                      if (isSelected) {
                        setNowSelectedCoupon(null)
                      } else {
                        setNowSelectedCoupon(coupon)
                      }
                    }}
                  >
                    <CouponCheckCircle checked={isSelected} />
                    <div>
                      <CouponName>{coupon.couponName}</CouponName>
                      <CouponDesc>
                        {`최대 ${
                          coupon.maxDiscountAmt ? plusComma(coupon.maxDiscountAmt) : plusComma(coupon.couponDiscountAmt)
                        }원 할인`}
                      </CouponDesc>
                      <CouponDiscount>{plusComma(coupon.couponDiscountAmt)}원</CouponDiscount>
                    </div>
                  </CouponLabel>
                </div>
              )
            })}
          </CouponBox>
        </ModalBody>
        <ModalFooter>
          <FooterButton type='button' $cancel onClick={closeModal}>
            취소
          </FooterButton>
          <FooterButton type='button' onClick={handleApplyCoupon}>
            확인
          </FooterButton>
        </ModalFooter>
      </ModalBox>
      <ModalBackground onClick={closeModal} closing={closing} />
    </ModalWrap>
  )
}

interface typeProps {
  closing?: boolean
}

export const ModalBackground = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'closing',
})<typeProps>`
  width: 100%;
  height: 100vh;
  z-index: 20;
  background-color: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  animation: ${({ closing }) => (closing ? fadeOut : fadeIn)} 0.3s ease-in-out;
`

export const ModalWrap = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: 900;
`

export const ModalBox = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'closing',
})<typeProps>`
  width: 100%;
  background-color: var(--colWhite);
  border-radius: 22px 22px 0 0;
  animation: ${({ closing }) => (closing ? slideDown : slideUp)} 0.3s ease-in-out;
  position: absolute;
  z-index: 30;
  display: flex;
  flex-direction: column;
  bottom: 0;

  @media screen and (min-width: 1024px) {
    width: 420px;
    min-height: 280px;
    max-height: 90vh;
    left: 50%;
    top: 50%;
    bottom: auto;
    transform: translate(-50%, -50%);
    border-radius: 22px;
    animation: ${({ closing }) => (closing ? fadeOut : fadeIn)} 0.3s ease-in-out;
    position: fixed;
  }
`

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18.5px 22px 0 33px;
  @media screen and (min-width: 1024px) {
    padding: 6px 10px 0 20px;
  }
`

export const HeaderTitle = styled.p`
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
`

export const HeaderCount = styled.span`
  display: inline-block;
  color: var(--colPointBlue);
  margin-left: 4px;
`

export const CloseButton = styled.button`
  width: 41px;
  height: 48px;
  padding: 12px 8px 12px 9px;
  background: none;
  border: none;
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
  }
`

export const ModalBody = styled.div``

export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  margin: 0 20px;
`

export const CouponBox = styled.div`
  display: flex;
  margin: 8px 20px;
  flex-direction: column;
  gap: 12px;
  max-height: 328px;
  min-height: 129px;
  overflow-y: auto;
`

const CouponCheckCircle = styled.span<{ checked: boolean }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  border: 1.5px solid ${({ checked }) => (checked ? 'var(--colPointBlue)' : 'var(--colGray3)')};
  background: ${({ checked }) => (checked ? 'var(--colPointBlue)' : '#fff')};
  margin-right: 16px;
  margin-top: 2px; // 추가: 타이틀과 수직 정렬 맞춤
  position: relative;
  transition: border-color 0.18s, background 0.18s;

  &:after {
    content: '';
    display: ${({ checked }) => (checked ? 'block' : 'none')};
    width: 8px;
    height: 4px;
    border-left: 2px solid #fff;
    border-bottom: 2px solid #fff;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -60%) rotate(-45deg);
  }
`

export const CouponRadio = styled.input`
  display: none;
`

export const CouponLabel = styled.label<{ $selected?: boolean }>`
  padding: 20px;
  border: 1px solid var(--colGray3);
  border-radius: 6px;
  display: flex;
  align-items: flex-start; // 변경: flex-start로 맞춤
  cursor: pointer;
  ${({ $selected }) =>
    $selected &&
    css`
      border: 1.5px solid var(--colPointBlue);
      background: rgba(25, 118, 210, 0.04);
    `}
  transition: border 0.18s, background 0.18s;
`

export const CouponName = styled.p`
  font-size: 15px;
  line-height: 20px;
  color: var(--colBlack);
`

export const CouponDesc = styled.span`
  font-size: 12px;
  line-height: 15px;
  color: var(--colGray6);
  display: block;
  margin-top: 4px;
`

export const CouponDiscount = styled.strong`
  font-size: 24px;
  line-height: 26px;
  color: var(--colPointBlue);
  display: block;
  margin-top: 8px;
`

export const FooterButton = styled.button<{ $cancel?: boolean }>`
  width: 100%;
  text-align: center;
  border-radius: 5px;
  font-size: 14px;
  ${({ $cancel }) =>
    $cancel
      ? css`
          border: 1px solid var(--colGray3);
          background: var(--colWhite);
          color: var(--colBlack);
          line-height: 46px;
        `
      : css`
          border: none;
          background: var(--colBlack);
          color: var(--colWhite);
          line-height: 48px;
        `}
  cursor: pointer;
  transition: background 0.18s, color 0.18s, border 0.18s;
`

export default OrderCouponModal
